import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Button, Modal, Alert } from "react-bootstrap";
import { Formik } from "formik";
import { sendPasswordResetEmail } from "firebase/auth";

import callApi from "../../api/callApi";
import { auth } from "../../firebase/config";
import './style.css';


const Login = () => {
    const navigate = useNavigate();
    const [loginError, setLoginError] = useState("");
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
    const [resetMessage, setResetMessage] = useState("");
    const [resetError, setResetError] = useState("");

    const checkUserToken = () => {
        const accessToken = localStorage.getItem('access_token');
        if (accessToken && accessToken !== 'undefined') {
            navigate('/data-extraction');
        }
        else {
            // Remove tokens in localStorage
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('expires_in');
        }
    };

    useEffect(() => {
      checkUserToken();
    }, []);

    const handleLogin = async (values, { setSubmitting }) => {
        setLoginError("");
        try {
            let url = `${process.env.REACT_APP_API_URL}/v1/portal/login`;
            const response = await callApi(url, 'POST', {
                email: values.email,
                password: values.password
            });
            // Store tokens in localStorage
            localStorage.setItem('access_token', response.data.access_token);
            localStorage.setItem('refresh_token', response.data.refresh_token);
            localStorage.setItem('expires_in', response.data.expires_in);
            navigate('/data-extraction');
        } catch (err) {
            const errorMessage = err.message || "Login failed. Please check your credentials.";
            // Remove tokens in localStorage
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('expires_in');
            setLoginError(errorMessage);
        }
        setSubmitting(false);
    };

    const validateLogin = (values) => {
        const errors = {};
        if (!values.email) {
            errors.email = "Required";
        } else if (!/\S+@\S+\.\S+/.test(values.email)) {
            errors.email = "Invalid email";
        }
        if (!values.password) {
            errors.password = "Required";
        }
        return errors;
    };

    const handleForgotPassword = async () => {
        setResetError("");
        setResetMessage("");
        
        if (!forgotPasswordEmail) {
            setResetError("Please enter your email address");
            return;
        }
        
        if (!/\S+@\S+\.\S+/.test(forgotPasswordEmail)) {
            setResetError("Please enter a valid email address");
            return;
        }

        try {
            await sendPasswordResetEmail(auth, forgotPasswordEmail);
            setResetMessage("Password reset email sent! Check your inbox.");
            setTimeout(() => {
                setShowForgotPassword(false);
                setResetMessage("");
                setForgotPasswordEmail("");
            }, 3000);
        } catch (error) {
            console.error("Error sending password reset email:", error);
            setResetError(error.message || "Failed to send password reset email");
        }
    };

    const handleCloseForgotPassword = () => {
        setShowForgotPassword(false);
        setResetError("");
        setResetMessage("");
        setForgotPasswordEmail("");
    };

    return (
        <div className="vh-100">
            <section id="signin" className="signup signin overflow-x-hidden">
                <div className="row d-flex align-items-center xl-vh-100">
                    <div className="col-lg-5 col-xl-4 w-600">
                        <div className="bg-image-dobin border-right xl-vh-100">
                            <div className="container px-0">
                                <div className="bg-lines">
                                    <div className="logo d-flex align-items-center pl-80">
                                        <img src="https://lender.dobin.io/static/img/logo-no-text.svg" alt="Logo" className="main-logo" />
                                    </div>
                                    <div className="title signin_title d-lg-block d-none">
                                        <h1>Dobin Portal</h1>
                                    </div>
                                    <div className="description sign-in-img pl-80 w-100 pt-73 d-lg-block d-none">
                                        <img src="https://lender.dobin.io/static/img/sign-in.svg" alt="" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-5 col-lg-6 w-800 m-pt-70">
                        <div className="row align-items-center justify-content-center tab-content">
                            <div className="col-lg-8 w-500">
                                <div className="title">
                                    <h2 className="text-start">Login to your account</h2>
                                </div>
                                <Formik
                                    initialValues={{ email: "", password: "" }}
                                    validate={validateLogin}
                                    onSubmit={handleLogin}
                                >
                                    {({
                                        handleSubmit,
                                        handleChange,
                                        values,
                                        touched,
                                        errors,
                                        isSubmitting,
                                    }) => (
                                        <Form noValidate onSubmit={handleSubmit} className="signup-form">
                                            <div className="row mt-40">
                                                <div className="col">
                                                    <Form.Control
                                                        type="email"
                                                        name="email"
                                                        value={values.email}
                                                        onChange={handleChange}
                                                        isInvalid={touched.email && !!errors.email}
                                                        placeholder="Email*"
                                                        className="form-control-lg"
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.email}
                                                    </Form.Control.Feedback>
                                                </div>
                                            </div>
                                            <div className="row mt-44">
                                                <div className="col">
                                                    <Form.Control
                                                        type="password"
                                                        name="password"
                                                        value={values.password}
                                                        onChange={handleChange}
                                                        isInvalid={touched.password && !!errors.password}
                                                        placeholder="Password*"
                                                        className="form-control-lg border-right-none"
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.password}
                                                    </Form.Control.Feedback>
                                                </div>
                                            </div>
                                            <div className="mb-3 mt-20 d-flex justify-content-start align-items-center" style={{ paddingLeft: 0 }}>
                                                <a 
                                                    href="#" 
                                                    className="forgot-pwd remember-label text-decoration-none"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setShowForgotPassword(true);
                                                    }}
                                                >
                                                    Forgot password?
                                                </a>
                                            </div>
                                            <div className="row mt-4">
                                                <div className="col-lg-12">
                                                    <div className="errors">
                                                        <span className="errorTxt">
                                                            {loginError && (
                                                                <div className="text-danger mt-4">{loginError}</div>
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row mt-28">
                                                <div className="col">
                                                    <Button
                                                        variant="primary"
                                                        type="submit"
                                                        disabled={isSubmitting}
                                                        className="btn-primary w-100"
                                                    >
                                                        Login
                                                    </Button>
                                                </div>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Forgot Password Modal */}
            <Modal show={showForgotPassword} onHide={handleCloseForgotPassword} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Reset Password</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {resetMessage && (
                        <Alert variant="success" className="mb-3">
                            {resetMessage}
                        </Alert>
                    )}
                    {resetError && (
                        <Alert variant="danger" className="mb-3">
                            {resetError}
                        </Alert>
                    )}
                    <Form.Group className="mb-3">
                        <Form.Label>Email address</Form.Label>
                        <Form.Control
                            type="email"
                            placeholder="Enter your email address"
                            value={forgotPasswordEmail}
                            onChange={(e) => setForgotPasswordEmail(e.target.value)}
                        />
                        <Form.Text className="text-muted">
                            We will send you a password reset link to this email address.
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleForgotPassword}>
                        Send Reset Email
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default Login;